import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI Components
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Slider from '@mui/material/Slider';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';

// Styles
import styles from './style.module.scss';

interface AudioControlsProps {
  isMuted: boolean;
  sfxVolume: number;
  onToggleMute: () => void;
  onSfxVolumeChange: (value: number) => void;
}

const AudioControls: React.FC<AudioControlsProps> = ({
  isMuted,
  sfxVolume,
  onToggleMute,
  onSfxVolumeChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        className={styles.audioButton}
        aria-label={t('games.snake.audio_controls')}
      >
        {isMuted ? (
          <VolumeOff className={styles.audioIcon} />
        ) : (
          <VolumeUp className={styles.audioIcon} />
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={styles.audioPopover}>
          <div className={styles.audioPopoverHeader}>
            <span className={styles.audioPopoverTitle}>
              {t('games.snake.audio')}
            </span>
            <button
              onClick={onToggleMute}
              className={styles.muteButton}
            >
              {isMuted ? t('games.snake.unmute') : t('games.snake.mute')}
            </button>
          </div>

          <div className={styles.audioSliderSection}>
            <div className={styles.audioSliderLabel}>
              <span className={styles.audioSliderIcon}>🔊</span>
              <span className={styles.audioSliderText}>
                {t('games.snake.sound_fx')}
              </span>
            </div>
            <Slider
              value={sfxVolume * 100}
              onChange={(_, value) => onSfxVolumeChange((value as number) / 100)}
              aria-labelledby="sfx-volume-slider"
              min={0}
              max={100}
              step={5}
              disabled={isMuted}
              sx={{
                color: '#00FF00',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#00FF00',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(0, 255, 0, 0.16)',
                  },
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#00FF00',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#666666',
                },
              }}
            />
            <div className={styles.audioSliderValue}>
              {Math.round(sfxVolume * 100)}%
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default AudioControls;

